/**
 * Master Inventory Configurations
 * Container component for managing bottles, caps, labels, and supplies configurations
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Grid, Card, CardContent, CardActions,
  IconButton, Chip, ToggleButtonGroup, ToggleButton, InputAdornment, Tooltip,
  alpha, useTheme, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Stack, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon,
  Search as SearchIcon, ViewList as ViewListIcon, ViewModule as ViewModuleIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon, Business as BusinessIcon,
  LocalOffer as LocalOfferIcon, Info as InfoIcon
} from '@mui/icons-material';
import { usePermissions } from '../../../utils/permissions';
import { useSuppliers } from './shared/useInventoryData';
import { supabase } from '../../../supabaseClient';
import FilterDialogs from './shared/FilterDialogs';
import ViewDialogue from './configuration-components/ViewDialogue';
import BottlesConfig from './configuration-components/BottlesConfig';
import CapsConfig from './configuration-components/CapsConfig';
import LabelsConfig from './configuration-components/LabelsConfig';
import SuppliesConfig from './configuration-components/SuppliesConfig';
import ChemicalsConfig from './configuration-components/ChemicalsConfig';
import ProductsConfig from './configuration-components/ProductsConfig';
import AllInventoryConfigs from './all-inventory/AllInventoryConfigs';

export default function InventoryConfigs({ companyId, inventoryType: initialType, onTypeChange }) {
  const theme = useTheme();
  const { canEditInventoryConfigurations, canDeleteInventoryConfigurations } = usePermissions();
  const canEdit   = canEditInventoryConfigurations();
  const canDelete = canDeleteInventoryConfigurations();

  // Ensure configType is always one of the valid values
  const validTypes = ['all', 'products', 'chemicals', 'caps', 'bottles', 'labels', 'supplies'];
  const getValidType = (type) => validTypes.includes(type) ? type : 'all';

  const [configType, setConfigType] = useState(getValidType(initialType));
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // View dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedItemDetail, setSelectedItemDetail] = useState(null);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [relatedDataInfo, setRelatedDataInfo] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Vendor filter dialog
  const [vendorFilterDialogOpen, setVendorFilterDialogOpen] = useState(false);
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [dialogSelectedVendor, setDialogSelectedVendor] = useState('');

  const { suppliers, loading: suppliersLoading } = useSuppliers(companyId);

  useEffect(() => {
    if (initialType && validTypes.includes(initialType)) {
      setConfigType(initialType);
    }
  }, [initialType]);

  useEffect(() => {
    fetchItems();
  }, [configType, companyId]);

  useEffect(() => {
    filterItems();
  }, [items, searchQuery, selectedVendor]);

  const configTypeInfo = {
    all:       { label: 'All Inventory', singular: '', table: '', description: '' },
    products:  { label: 'Products',  singular: 'Product',  table: 'products',      description: 'Manage unit configurations for product inventory — cases, pallets, or units only' },
    bottles:   { label: 'Bottles',   singular: 'Bottle',   table: 'bottles',       description: 'Manage bottles catalog, track specifications, vendor information, and unit configurations' },
    caps:      { label: 'Caps',      singular: 'Cap',      table: 'caps',          description: 'Manage caps catalog, track specifications, vendor information, and unit configurations' },
    labels:    { label: 'Labels',    singular: 'Label',    table: 'labels',        description: 'Manage labels catalog, track specifications, vendor information, and unit configurations' },
    supplies:  { label: 'Supplies',  singular: 'Supply',   table: 'supplies',      description: 'Manage supplies catalog, track specifications, vendor information, and unit configurations' },
    chemicals: { label: 'Chemicals', singular: 'Chemical', table: 'raw_materials', description: 'Manage raw materials catalog, track unit types, unit measures, hazard info, and vendor configurations' }
  };

  const fetchItems = async () => {
    // Products, Chemicals, and All have their own management UI and fetch their own data
    if (configType === 'all' || configType === 'products' || configType === 'chemicals') { setInitialLoading(false); return; }
    try {
      const typeInfo = configTypeInfo[configType];
      if (!typeInfo) {
        console.error('Invalid config type:', configType);
        setInitialLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from(typeInfo.table)
        .select('*, supplier:supplier_id(id, name)')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setItems(data || []);
    } catch (err) {
      console.error('Error loading items:', err);
    } finally {
      setInitialLoading(false);
    }
  };

  const filterItems = () => {
    let filtered = items;

    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.material?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedVendor) {
      filtered = filtered.filter(item => item.supplier_id === selectedVendor);
    }

    setFilteredItems(filtered);
    setPage(0);
  };

  const handleApplyVendorFilter = () => {
    setSelectedVendor(dialogSelectedVendor);
    setVendorFilterDialogOpen(false);
  };

  const handleRowClick = (item) => {
    setSelectedItemDetail(item);
    setViewDialogOpen(true);
  };

  const handleOpenDialog = (item = null) => {
    setEditingItem(item);
    setDialogOpen(true);
  };

  const checkRelatedData = async (itemId) => {
    try {
      const inventoryTable = `${configType}_inventory`;
      const transactionsTable = `${configType}_transactions`;
      const productTable = `product_${configType}`;

      const [inventoryCount, transactionCount, associationCount] = await Promise.all([
        supabase
          .from(inventoryTable)
          .select('id', { count: 'exact' })
          .eq(`${configType.slice(0, -1)}_id`, itemId),
        supabase
          .from(transactionsTable)
          .select('id', { count: 'exact' })
          .eq(`${configType.slice(0, -1)}_id`, itemId),
        supabase
          .from(productTable)
          .select('id', { count: 'exact' })
          .eq(`${configType.slice(0, -1)}_id`, itemId)
      ]);

      return {
        inventoryRecords: inventoryCount.count || 0,
        transactionRecords: transactionCount.count || 0,
        associationRecords: associationCount.count || 0,
        totalRecords: (inventoryCount.count || 0) + (transactionCount.count || 0) + (associationCount.count || 0) + 1
      };
    } catch (err) {
      console.error('Error checking related data:', err);
      return { inventoryRecords: 0, transactionRecords: 0, associationRecords: 0, totalRecords: 1 };
    }
  };

  const handleDeleteClick = async (item) => {
    const relatedData = await checkRelatedData(item.id);
    setItemToDelete(item);
    setRelatedDataInfo(relatedData);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setSubmitLoading(true);

      const inventoryTable = `${configType}_inventory`;
      const transactionsTable = `${configType}_transactions`;
      const productTable = `product_${configType}`;
      const itemIdColumn = `${configType.slice(0, -1)}_id`;

      // Delete all related records first
      await Promise.all([
        supabase.from(inventoryTable).delete().eq(itemIdColumn, itemToDelete.id),
        supabase.from(transactionsTable).delete().eq(itemIdColumn, itemToDelete.id),
        supabase.from(productTable).delete().eq(itemIdColumn, itemToDelete.id)
      ]);

      // Delete the item
      const typeInfo = configTypeInfo[configType];
      const { error } = await supabase
        .from(typeInfo.table)
        .delete()
        .eq('id', itemToDelete.id);

      if (error) throw error;

      setDeleteDialogOpen(false);
      setItemToDelete(null);
      setRelatedDataInfo(null);
      fetchItems();
    } catch (err) {
      console.error('Error deleting item:', err);
    } finally {
      setSubmitLoading(false);
    }
  };

  const getPaginatedItems = () => {
    const startIndex = page * rowsPerPage;
    return filteredItems.slice(startIndex, startIndex + rowsPerPage);
  };

  const renderItemCard = (item) => (
    <Card
      key={item.id}
      onClick={() => handleRowClick(item)}
      sx={{
        height: '100%',
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: 'white',
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.3)}`,
          border: `1px solid ${theme.palette.primary.main}`
        }
      }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 500, mb: 2, color: 'text.primary', fontSize: '1.3rem', lineHeight: 1.2 }}>
          {item.name}
        </Typography>
        {item.sku && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontWeight: 500, fontSize: '0.95rem' }}>
            SKU: {item.sku}
          </Typography>
        )}
        <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
          {item.material && <Chip label={item.material} size="medium" variant="outlined" sx={{ fontWeight: 500, fontSize: '0.85rem', height: 32 }} />}
          {item.color && <Chip label={item.color} size="medium" variant="outlined" sx={{ fontWeight: 500, fontSize: '0.85rem', height: 32 }} />}
        </Stack>
        {item.supplier && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <BusinessIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.95rem' }}>
              {item.supplier.name}
            </Typography>
          </Box>
        )}
        {item.unit_cost && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LocalOfferIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.95rem' }}>
              ${parseFloat(item.unit_cost).toFixed(2)}
            </Typography>
          </Box>
        )}
      </CardContent>
      <CardActions sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          size="medium"
          startIcon={<EditIcon />}
          disabled={!canEdit}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenDialog(item);
          }}
          sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: 500, fontSize: '0.9rem' }}
        >
          Edit
        </Button>
        <Button
          variant="outlined"
          size="medium"
          color="error"
          startIcon={<DeleteIcon />}
          disabled={!canDelete}
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(item);
          }}
          sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: 500, fontSize: '0.9rem' }}
        >
          Delete
        </Button>
      </CardActions>
    </Card>
  );

  const renderTableView = () => (
    <Paper sx={{
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
      overflow: 'hidden',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
      }
    }}>
      <TableContainer sx={{ overflow: 'hidden' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{
                fontWeight: 500,
                fontSize: '0.9rem',
                bgcolor: 'white',
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                color: 'primary.main',
                py: 2,
                px: 3
              }}>Name</TableCell>
              <TableCell sx={{
                fontWeight: 500,
                fontSize: '0.9rem',
                bgcolor: 'white',
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                color: 'primary.main',
                py: 2,
                px: 3
              }}>SKU</TableCell>
              <TableCell sx={{
                fontWeight: 500,
                fontSize: '0.9rem',
                bgcolor: 'white',
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                color: 'primary.main',
                py: 2,
                px: 3
              }}>Material</TableCell>
              <TableCell sx={{
                fontWeight: 500,
                fontSize: '0.9rem',
                bgcolor: 'white',
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                color: 'primary.main',
                py: 2,
                px: 3
              }}>Vendor</TableCell>
              <TableCell sx={{
                fontWeight: 500,
                fontSize: '0.9rem',
                bgcolor: 'white',
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                color: 'primary.main',
                py: 2,
                px: 3
              }}>Cost</TableCell>
              <TableCell align="center" sx={{
                fontWeight: 500,
                fontSize: '0.9rem',
                bgcolor: 'white',
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                color: 'primary.main',
                py: 2,
                px: 3,
                width: 120
              }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getPaginatedItems().map((item) => (
              <TableRow
                key={item.id}
                onClick={() => handleRowClick(item)}
                hover
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  bgcolor: 'white',
                  '&:hover': {
                    bgcolor: 'grey.50',
                    transform: 'translateX(4px)',
                    transition: 'all 0.2s ease',
                    boxShadow: 'inset 4px 0 0 black'
                  },
                  '& .MuiTableCell-root': {
                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                    py: 3,
                    px: 3
                  }
                }}
              >
                <TableCell>
                  <Typography variant="h6" sx={{
                    fontWeight: 400,
                    color: 'text.primary',
                    fontSize: '1.1rem',
                    mb: 0.5
                  }}>
                    {item.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    fontSize: '0.95rem'
                  }}>
                    {item.sku || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    fontSize: '0.95rem'
                  }}>
                    {item.material || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.95rem'
                  }}>
                    {item.supplier?.name || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.95rem'
                  }}>
                    {item.unit_cost ? `$${parseFloat(item.unit_cost).toFixed(2)}` : '-'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <IconButton
                      size="medium"
                      disabled={!canEdit}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDialog(item);
                      }}
                      sx={{
                        color: 'primary.main',
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                        borderRadius: 2,
                        transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                        '&:hover': {
                          bgcolor: 'primary.main',
                          color: 'white',
                          transform: 'scale(1.05)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                        }
                      }}
                    >
                      <EditIcon fontSize="medium" />
                    </IconButton>
                    <IconButton
                      size="medium"
                      disabled={!canDelete}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(item);
                      }}
                      sx={{
                        color: 'error.main',
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                        borderRadius: 2,
                        transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                        '&:hover': {
                          bgcolor: 'error.main',
                          color: 'white',
                          transform: 'scale(1.05)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`
                        }
                      }}
                    >
                      <DeleteIcon fontSize="medium" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  const configDialogProps = {
    companyId,
    suppliers,
    open: dialogOpen,
    onClose: () => { setDialogOpen(false); setEditingItem(null); fetchItems(); },
    editingItem
  };

  const typeSelector = (
    <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
        Inventory Type:
      </Typography>
      <Select
        value={configType}
        onChange={(e) => { setConfigType(e.target.value); if (onTypeChange) onTypeChange(e.target.value); }}
        size="small"
        sx={{
          minWidth: 200, borderRadius: 3, bgcolor: 'white',
          '& .MuiOutlinedInput-notchedOutline': { border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` },
          '&:hover .MuiOutlinedInput-notchedOutline': { border: `1px solid ${theme.palette.primary.main}` },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: `1px solid ${theme.palette.primary.main}` }
        }}
      >
        {validTypes.map((key) => (
          <MenuItem key={key} value={key}>{configTypeInfo[key].label}</MenuItem>
        ))}
      </Select>
    </Box>
  );

  // All Inventory — unified view of all 6 config types
  if (configType === 'all') {
    return (
      <Box sx={{ p: 3 }}>
        {typeSelector}
        <AllInventoryConfigs companyId={companyId} canEdit={canEdit} canDelete={canDelete} />
      </Box>
    );
  }

  // Products has its own unit configurations UI
  if (configType === 'products') {
    return (
      <Box sx={{ p: 3 }}>
        {typeSelector}
        <ProductsConfig companyId={companyId} canEdit={canEdit} canDelete={canDelete} />
      </Box>
    );
  }

  // Chemicals has its own full management UI — render it with just the type selector
  if (configType === 'chemicals') {
    return (
      <Box sx={{ p: 3 }}>
        {typeSelector}
        <ChemicalsConfig companyId={companyId} suppliers={suppliers} canEdit={canEdit} canDelete={canDelete} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Inventory Type Selector */}
      {typeSelector}

      {/* Header */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, p: 3,
        borderRadius: 4, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: 'white',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`, position: 'relative', overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}` }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1.8rem', lineHeight: 1.2, mb: 1 }}>
            {configTypeInfo[configType].label} Configuration
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.9rem', mb: 2.5, maxWidth: '500px' }}>
            {configTypeInfo[configType].description}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Chip label={`${items.length} ${configTypeInfo[configType].label}`} size="small"
              sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }} />
            <Chip label={`${suppliers.length} Vendors`} size="small"
              sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <Tooltip title="Refresh data">
            <IconButton onClick={fetchItems}
              sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, color: 'primary.main' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}
            disabled={!canEdit}
            sx={{ borderRadius: 2, px: 3, py: 1.2, fontSize: '0.9rem', fontWeight: 500, boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`, whiteSpace: 'nowrap', '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500', boxShadow: 'none' } }}>
            Add {configTypeInfo[configType].singular}
          </Button>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{
        p: 4,
        mb: 4,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }
      }}>
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="body2" sx={{
                mb: 1,
                fontWeight: 500,
                color: 'text.secondary',
                fontSize: '0.9rem'
              }}>
                Search
              </Typography>
              <TextField
                fullWidth
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    height: 56,
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '1rem',
                    fontWeight: 500,
                    py: 2,
                    px: 2
                  },
                  '& .MuiOutlinedInput-root': {
                    height: 56,
                    '& fieldset': { border: 'none' },
                    '&:hover fieldset': { border: 'none' },
                    '&.Mui-focused fieldset': { border: 'none' }
                  }
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box>
              <Typography variant="body2" sx={{
                mb: 1,
                fontWeight: 500,
                color: 'text.secondary',
                fontSize: '0.9rem'
              }}>
                Vendor
              </Typography>
              <Button
                onClick={() => setVendorFilterDialogOpen(true)}
                variant="outlined"
                fullWidth
                sx={{
                  height: 56,
                  borderRadius: 3,
                  backgroundColor: 'white',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  color: selectedVendor ? 'text.primary' : 'text.secondary',
                  fontWeight: 500,
                  justifyContent: 'space-between',
                  textTransform: 'none',
                  px: 2,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}
              >
                <Typography sx={{ fontSize: '1rem' }}>
                  {selectedVendor ? suppliers.find(s => s.id === selectedVendor)?.name : 'All Vendors'}
                </Typography>
                <KeyboardArrowDownIcon sx={{ color: 'text.secondary' }} />
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box>
              <Typography variant="body2" sx={{
                mb: 1,
                fontWeight: 500,
                color: 'text.secondary',
                fontSize: '0.9rem'
              }}>
                View Mode
              </Typography>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, v) => v && setViewMode(v)}
                sx={{
                  height: 56,
                  gap: 1,
                  '& .MuiToggleButton-root': {
                    borderRadius: 3,
                    px: 3,
                    py: 2,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    '&.Mui-selected': {
                      bgcolor: theme.palette.grey[200],
                      color: 'text.primary',
                      '&:hover': {
                        bgcolor: theme.palette.grey[300]
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="grid"><ViewModuleIcon /></ToggleButton>
                <ToggleButton value="table"><ViewListIcon /></ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              textAlign: 'center',
              cursor: 'default',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}>
              <Typography variant="h6" sx={{
                fontWeight: 500,
                color: 'primary.main',
                fontSize: '1.1rem'
              }}>
                {filteredItems.length}
              </Typography>
              <Typography variant="body2" sx={{
                color: 'black',
                fontWeight: 500,
                fontSize: '0.9rem'
              }}>
                found
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Items Display */}
      {viewMode === 'grid' ? (
        <Grid container spacing={4}>
          {getPaginatedItems().map((item) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
              {renderItemCard(item)}
            </Grid>
          ))}
        </Grid>
      ) : (
        renderTableView()
      )}

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TablePagination component="div" count={filteredItems.length} page={page} onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[12, 24, 48]} />
        </Paper>
      </Box>

      {/* Filter Dialogs */}
      <FilterDialogs
        vendorFilterDialogOpen={vendorFilterDialogOpen} setVendorFilterDialogOpen={setVendorFilterDialogOpen}
        vendorSearchTerm={vendorSearchTerm} setVendorSearchTerm={setVendorSearchTerm}
        suppliers={suppliers} dialogSelectedVendor={dialogSelectedVendor} setDialogSelectedVendor={setDialogSelectedVendor}
        onApplyVendorFilter={handleApplyVendorFilter}
      />

      {/* Add/Edit Dialogs */}
      {configType === 'bottles'  && <BottlesConfig  {...configDialogProps} />}
      {configType === 'caps'     && <CapsConfig     {...configDialogProps} />}
      {configType === 'labels'   && <LabelsConfig   {...configDialogProps} />}
      {configType === 'supplies' && <SuppliesConfig {...configDialogProps} />}

      {/* View Dialog */}
      <ViewDialogue
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedItemDetail(null);
        }}
        item={selectedItemDetail}
        itemType={configType}
        onEdit={handleOpenDialog}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DeleteIcon color="error" />
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.primary' }}>
            Are you sure you want to delete <strong>"{itemToDelete?.name}"</strong>?
          </Typography>
          {relatedDataInfo && relatedDataInfo.totalRecords > 1 && (
            <Box sx={{
              p: 2.5,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'grey.50'
            }}>
              <Typography variant="subtitle2" sx={{
                fontWeight: 500,
                mb: 2,
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <InfoIcon fontSize="small" />
                Related data will also be deleted
              </Typography>
              <Grid container spacing={2}>
                {relatedDataInfo.inventoryRecords > 0 && (
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <Typography variant="h6" color="text.primary">
                        {relatedDataInfo.inventoryRecords}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Inventory Record{relatedDataInfo.inventoryRecords !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {relatedDataInfo.transactionRecords > 0 && (
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <Typography variant="h6" color="text.primary">
                        {relatedDataInfo.transactionRecords}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Transaction{relatedDataInfo.transactionRecords !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {relatedDataInfo.associationRecords > 0 && (
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <Typography variant="h6" color="text.primary">
                        {relatedDataInfo.associationRecords}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Product Link{relatedDataInfo.associationRecords !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                  {relatedDataInfo.totalRecords} total records will be permanently deleted
                </Typography>
              </Box>
            </Box>
          )}
          <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary', fontStyle: 'italic' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={submitLoading}
            sx={{ borderRadius: 2 }}
          >
            {submitLoading ? 'Deleting...' : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
